<template>
  <div>
    <sl-alert v-show="invitationsTemporarelyDisabled" dismissible type="warning">
      You've consecutively invited more than {{ invitationsConsecutiveMax }} users within a
      {{ invitationsConsecutiveMaxTimeframe / 1000 }} seconds timeframe. This is considered
      suspicious. Sorry, the invitation button will now be disabled for
      {{ invitationsTemporarelyDisabledPeriod / 1000 }} seconds.
    </sl-alert>
    <sl-dialog-confirm
      v-model="workspaceUserRemoveDialogShow"
      @cancel="workspaceUserRemoveCancel"
      @confirm="workspaceUserRemoveConfirm"
    >
      <template #title>
        Confirm exclude {{ workspaceUserRemoveCandidate.firstName }}
        {{ workspaceUserRemoveCandidate.lastName }}
        from {{ workspace.title }}
      </template>
      The user will leave the workspace and loose access to all its checklists. His/Her logged
      activities will still be available to the other workspace members.
    </sl-dialog-confirm>
    <v-form ref="form" v-model="formIsValid">
      <v-simple-table>
        <template v-slot:default>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td>{{ user.email }}</td>
              <td>{{ user.firstName }}</td>
              <td>{{ user.lastName }}</td>
              <td>
                <div v-if="user.termsAccepted">
                  {{ accessTypeLabel[user.accessType] }}
                </div>
                <div v-else>
                  Invited on
                  <sl-time :time="user.invitationTime"></sl-time>
                </div>
              </td>
              <td v-if="userAccess === 'admin'">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="user.accessType == 'member'"
                      icon
                      color="warning"
                      @click.stop="workspaceUserRemoveAskConfirmation(user)"
                      v-bind="attrs"
                      v-on="on"
                    >
                      <v-icon>mdi-account-remove</v-icon>
                    </v-btn>
                  </template>
                  <span>Remove access to workspace contents</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on">
                      <v-btn
                        v-if="!user.termsAccepted"
                        :disabled="invitationsTemporarelyDisabled"
                        icon
                        v-bind="attrs"
                        @click="workspaceUserSendInvitation(user)"
                      >
                        <v-icon>mdi-send</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span v-if="invitationsTemporarelyDisabled">
                    Temporarily disabled: try again in a few seconds
                  </span>
                  <span v-else>Resend invitation</span>
                </v-tooltip>
              </td>
            </tr>
            <tr v-if="userAccess === 'admin'" class="grey lighten-4 pb-0 valign-bottom">
              <td colspan="6" class="pa-0 pt-5 pl-1" style="border: none">Invite a new user</td>
            </tr>
            <tr v-if="userAccess === 'admin'" class="grey lighten-4 pt-0">
              <td class="px-0">
                <sl-text-field
                  :rules="rules.email"
                  v-model="userToAdd.email"
                  id="email"
                  label="Email"
                  name="email"
                  type="email"
                  required
                  class="mt-2"
                  dense
                />
              </td>
              <td class="px-0">
                <sl-text-field
                  v-model="userToAdd.firstName"
                  id="firstName"
                  label="First name"
                  name="firstName"
                  class="mt-2"
                  dense
                />
              </td>
              <td class="px-0">
                <sl-text-field
                  v-model="userToAdd.lastName"
                  id="lastName"
                  label="Last name"
                  name="lastName"
                  class="mt-2"
                  dense
                />
              </td>

              <td colspan="2">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on">
                      <v-btn
                        :disabled="
                          !formIsValid ||
                            invitationsTemporarelyDisabled ||
                            workspaceUsersNumberMaxReached
                        "
                        color="primary"
                        icon
                        v-bind="attrs"
                        @click="workspaceUserAdd()"
                        class="mb-4"
                      >
                        <v-icon>mdi-account-plus</v-icon>
                      </v-btn>
                    </span>
                  </template>
                  <span v-if="invitationsTemporarelyDisabled">
                    Temporarily disabled: try again in a few seconds
                  </span>
                  <span v-if="workspaceUsersNumberMaxReached">
                    Sorry, you have reached the maximum number ({{
                      userLogged.usageLimits.workspaceUsersNumberMax - 1
                    }}) of guests you can invite in your shared workspaces; contact us to extend
                    this limit
                  </span>
                  <span v-else>Invite and grant access to workspace contents</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-form>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import Logger from '@/lib/Logger'

import Workspace from '@/models/Workspace'

import SlDialogConfirm from '@/components/SlDialogConfirm'
import SlTime from '@/components/SlTime'
import SlTextField from '@/components/SlTextField'
import SlAlert from '@/components/SlAlert'

export default {
  props: {
    userAccess: {
      type: String,
      required: true,
      validator: function(value) {
        // The value must match one of these strings
        return ['private', 'admin', 'member'].indexOf(value) !== -1
      },
    },
    workspace: {
      type: Object,
      required: true,
    },
  },

  components: {
    SlDialogConfirm,
    SlTime,
    SlTextField,
    SlAlert,
  },

  data: () => ({
    users: [
      /* {
        id: 'a',
        email: 'a@b.c',
        firstName: 'Aaaaa',
        lastName: 'Bbbbb',
        status: 'Admin',
        invitationTime: '',
      },
      {
        id: 'b',
        email: 'b@c.d',
        firstName: 'Bbbbb',
        lastName: 'Ccccc',
        status: 'Member',
        invitationTime: '',
      },
      {
        id: 'd',
        email: 'b@c.d',
        firstName: 'Bbbbb',
        lastName: 'Ccccc',
        status: 'Member',
        invitationTime: '',
      },
      {
        id: 'c',
        email: 'c@d.e',
        firstName: 'Ccccc',
        lastName: 'Ddddd',
        status: 'member',
        invitationTime: 'July 1st, 2020',
      }, */
    ],
    userToAdd: {
      email: '',
      firstName: '',
      lastName: '',
    },
    invitationsConsecutive: 0,
    invitationsConsecutiveFirstTimestamp: 0,
    invitationsConsecutiveMax: 10,
    invitationsConsecutiveMaxTimeframe: 10000, // ms
    invitationsTemporarelyDisabled: false,
    invitationsTemporarelyDisabledPeriod: 60000, // ms
    formIsValid: false,
    rules: {
      email: [v => !!v || 'Required', v => /.+@.+\..+/.test(v) || 'E-mail must be valid'],
    },
    workspaceUserRemoveCandidate: {},
    workspaceUserRemoveDialogShow: false,
    accessTypeLabel: {
      admin: 'Admin',
      member: 'Member',
      private: 'Admin',
    },
  }),

  computed: {
    workspaceUsersNumberMaxReached() {
      return !this.workspace.actionsAllowed?.userInvite
    },
    ...mapState({
      userLogged: state => state.userLogged,
    }),
  },

  created() {
    this.queries = [
      Workspace.find({
        get: `{
                id
                name
                wsAccesses {
                  invitationTime
                  accessType
                  user { id, email, firstName, lastName, termsAccepted}
                }                
              }`,
        by: { id: this.workspace.id },
        onChange: data => {
          this.users = data.wsAccesses.map(x => {
            return {
              invitationTime: x.invitationTime,
              accessType: x.accessType,
              ...x.user,
            }
          })
        },
        onError: error => {
          Logger.error('ERROR in loading data:', error)
          this.toastSet({
            message: 'Error in loading data',
            color: 'error',
          })
        },
      }),
    ]
  },

  beforeDestroy() {
    this.queries.forEach(q => q.destroy())
  },

  methods: {
    async workspaceUserAdd() {
      this.userConsecutiveInvitationsCheck()
      if (this.userAlreadyInWorkspace(this.userToAdd)) {
        this.toastSet({
          message: this.userToAdd.email + ' is already member of ' + this.workspace.name,
          color: 'warning',
        })
        this.userAddFormReset()
      } else {
        Logger.debug('workspaceUserAdd', this.userToAdd)
        try {
          const userAdded = await Workspace.userAdd(this.workspace, this.userToAdd)
          Logger.debug('userAdded:', userAdded)
          this.userAddFormReset()
          if (userAdded.termsAccepted) {
            // L'utente era già registrato su Sharylist, quindi è stato aggiunto sul WS
            this.toastSet({
              message: userAdded.email + ' successfully added to ' + this.workspace.name,
              color: 'success',
            })
            this.$gtag.event('userWsAuthorise', {
              event_category: 'engagement',
            })
          } else {
            // L'utente non era registrato su Sharylist, quindi è stato invitato
            this.toastSet({
              message: 'Invitation sent to ' + userAdded.email,
              color: 'success',
            })
            this.$gtag.event('userInvitation', {
              event_category: 'engagement',
            })
          }
        } catch (e) {
          /* if (e.name == 'USER_NOT_REGISTERED') {
            // FIXME: this exception is never raised, apparently!!!
            await this.workspaceUserSendInvitation(this.userToAdd)
            this.userAddFormReset()
          } else { */
          Logger.error(e)
          this.toastSet({
            message: 'Error in adding ' + this.userToAdd.email + ' to ' + this.workspace.name,
            color: 'error',
          })
          this.$gtag.exception({
            description: 'workspaceUserAdd unknown falure: ' + this.userToAdd.email + e,
            fatal: false,
          })
          // }
        }
      }
    },
    userAddFormReset() {
      this.userToAdd = {
        email: '',
        firstName: '',
        lastName: '',
      }
      this.$refs.form.resetValidation()
    },
    userAlreadyInWorkspace(userCheked) {
      const sameEmail = user => user.email === userCheked.email
      return this.users.some(sameEmail)
    },
    workspaceUserRemoveAskConfirmation(user) {
      this.workspaceUserRemoveDialogShow = true
      this.workspaceUserRemoveCandidate = user
    },
    workspaceUserRemoveCancel() {
      this.workspaceUserRemoveDialogShow = false
      this.workspaceUserRemoveCandidate = {}
    },
    async workspaceUserRemoveConfirm() {
      Logger.debug('workspaceUserRemove', this.workspaceUserRemoveCandidate)
      this.workspaceUserRemoveDialogShow = false
      try {
        await Workspace.userRemove(this.workspace, this.workspaceUserRemoveCandidate)
      } catch (e) {
        Logger.error(e)
        this.toastSet({
          message:
            'Error in removing user ' +
            this.workspaceUserRemoveCandidate.email +
            ' from ' +
            this.workspace.name,
          color: 'error',
        })
      }
    },
    async workspaceUserSendInvitation(user) {
      this.userConsecutiveInvitationsCheck()
      try {
        const userInvited = await Workspace.userInvite(this.workspace.id, user)
        this.toastSet({
          message: 'Invitation sent to ' + userInvited.email,
          color: 'success',
        })
      } catch (e) {
        Logger.error(e)
        this.toastSet({
          message: 'Error in inviting ' + user.email,
          color: 'error',
        })
      }
    },
    userConsecutiveInvitationsCheck() {
      this.invitationsConsecutive += 1
      Logger.debug('userConsecutiveInvitationsCheck', this.invitationsConsecutive)
      if (this.invitationsConsecutive > this.invitationsConsecutiveMax) {
        Logger.debug('invitationsConsecutiveMax Reached')
        const invitationsConsecutiveFirstTimeSince =
          Date.now() - this.invitationsConsecutiveFirstTimestamp
        if (invitationsConsecutiveFirstTimeSince < this.invitationsConsecutiveMaxTimeframe) {
          Logger.debug('within invitationsConsecutiveMaxTimeframe')
          this.invitationsTemporarelyDisabled = true
          // FIXME: ho tolto il toast, mettendo al suo posto un alert, perché non ho una libreria di toast che mi
          // permetta di visualizzare più toast contemporaneamente e qui succede. Per ora risolvo così...
          /* this.toastSet({
            message: `You've consecutively invited more than
            ${this.invitationsConsecutiveMax}
            users within a
            ${this.invitationsConsecutiveMaxTimeframe / 1000}
            seconds timeframe. This is considered suspicious.
            Sorry, the invitation button will now be disabled for
            ${this.invitationsTemporarelyDisabledPeriod / 1000}
            seconds.`,
            color: 'warning',
          }) */
          setTimeout(() => {
            this.invitationsTemporarelyDisabled = false
            this.invitationsConsecutive = 0
            this.invitationsConsecutiveFirstTimestamp = 0
          }, this.invitationsTemporarelyDisabledPeriod)
        } else {
          this.invitationsConsecutive = 1
          this.invitationsConsecutiveFirstTimestamp = Date.now()
        }
      } else {
        if (!this.invitationsConsecutiveFirstTimestamp)
          this.invitationsConsecutiveFirstTimestamp = Date.now()
      }
    },
    ...mapMutations(['toastSet']),
  },
}
</script>

<style scoped>
tr:hover {
  background-color: inherit !important;
}
</style>
